import type { OfflineProgress, Season, TimeState } from '../types';

export interface TimeManagerConfig {
  ticksPerGameMinute: number;
  daysPerSeason: number;
  realMinutesPerGameDay: number;
  maximumOfflineHours: number;
  /** オフライン復帰時に実際へ回す庭の日次処理の上限（ゲーム内日数）。 */
  maximumOfflineDays: number;
  /** オフライン復帰時にカレンダーが進む上限（ゲーム内日数）。 */
  maximumOfflineCalendarDays: number;
}

export type TimeScale = 1 | 2 | 5;
export type TimeEvent =
  | { type: 'minute'; state: Readonly<TimeState> }
  | { type: 'day'; state: Readonly<TimeState> }
  | { type: 'season'; previous: Season; current: Season; state: Readonly<TimeState> }
  | { type: 'year'; state: Readonly<TimeState> };

const SEASONS: readonly Season[] = ['spring', 'summer', 'autumn', 'winter'];
const MINUTES_PER_DAY = 1440;
const MORNING_MINUTE = 360;
const DEFAULT_CONFIG: TimeManagerConfig = { ticksPerGameMinute: 1, daysPerSeason: 7, realMinutesPerGameDay: 3, maximumOfflineHours: 72, maximumOfflineDays: 3, maximumOfflineCalendarDays: 10 };

export class TimeManager {
  readonly config: Readonly<TimeManagerConfig>;
  private state: TimeState;
  private tickRemainder = 0;
  /** UI 都合の一時停止。TimeState には含めず保存もしない。 */
  private suspended = false;
  private readonly listeners = new Set<(event: TimeEvent) => void>();

  constructor(initialState?: Partial<TimeState>, config: Partial<TimeManagerConfig> = {}) {
    this.config = Object.freeze({ ...DEFAULT_CONFIG, ...config });
    if (this.config.daysPerSeason < 1 || this.config.realMinutesPerGameDay <= 0 || this.config.ticksPerGameMinute <= 0) throw new RangeError('TimeManagerの設定値は正の数である必要があります。');
    this.state = { totalTicks: 0, day: 1, daysInSeason: 1, minuteOfDay: MORNING_MINUTE, season: 'spring', yearNumber: 1, lastRealTimestamp: Date.now(), timeScale: 1, paused: false, ...initialState };
    this.normalizeState();
  }

  get snapshot(): Readonly<TimeState> { return Object.freeze({ ...this.state }); }
  subscribe(listener: (event: TimeEvent) => void): () => void { this.listeners.add(listener); return () => this.listeners.delete(listener); }

  setTimeScale(scale: TimeScale): void {
    if (scale !== 1 && scale !== 2 && scale !== 5) throw new RangeError('時間倍率は1、2、5のいずれかを指定してください。');
    this.state.timeScale = scale;
  }

  /** 庭ごとの休止設定。セーブに残り、オフライン中も時間が進まなくなる。 */
  pause(): void { this.state.paused = true; }
  resume(now = Date.now()): void { this.state.paused = false; this.state.lastRealTimestamp = now; }

  /**
   * フォトモードなど UI 都合の一時停止。保存しない。
   * 以前はこれにも paused を使っていたため、モーダルを開いたままタブを閉じると
   * paused=true が保存され、次回以降ずっと時計が止まったままになっていた。
   */
  suspend(): void { this.suspended = true; }
  unsuspend(now = Date.now()): void { this.suspended = false; this.state.lastRealTimestamp = now; }
  get isPaused(): boolean { return this.state.paused; }

  update(realDeltaMilliseconds: number): TimeEvent[] {
    if (this.state.paused || this.suspended || realDeltaMilliseconds <= 0) return [];
    const gameMinutes = (realDeltaMilliseconds / 60000) * (MINUTES_PER_DAY / this.config.realMinutesPerGameDay) * this.state.timeScale;
    this.tickRemainder += gameMinutes * this.config.ticksPerGameMinute;
    const wholeTicks = Math.floor(this.tickRemainder);
    this.tickRemainder -= wholeTicks;
    return this.advanceTicks(wholeTicks);
  }

  advanceTicks(ticks: number): TimeEvent[] {
    if (!Number.isFinite(ticks) || ticks <= 0) return [];
    const events: TimeEvent[] = [];
    for (let index = 0; index < Math.floor(ticks); index += 1) {
      this.state.totalTicks += 1;
      if (this.state.totalTicks % this.config.ticksPerGameMinute !== 0) continue;
      this.state.minuteOfDay += 1;
      this.push(events, { type: 'minute', state: this.snapshot });
      if (this.state.minuteOfDay < MINUTES_PER_DAY) continue;
      this.state.minuteOfDay = 0;
      this.state.day += 1;
      this.state.daysInSeason += 1;
      this.push(events, { type: 'day', state: this.snapshot });
      if (this.state.daysInSeason <= this.config.daysPerSeason) continue;
      this.state.daysInSeason = 1;
      const previous = this.state.season;
      this.state.season = SEASONS[(SEASONS.indexOf(previous) + 1) % SEASONS.length] ?? 'spring';
      if (this.state.season === 'spring') { this.state.yearNumber += 1; this.push(events, { type: 'year', state: this.snapshot }); }
      this.push(events, { type: 'season', previous, current: this.state.season, state: this.snapshot });
    }
    return events;
  }

  skipToNextMorning(): TimeEvent[] {
    const minutes = this.state.minuteOfDay < MORNING_MINUTE ? MORNING_MINUTE - this.state.minuteOfDay : MINUTES_PER_DAY - this.state.minuteOfDay + MORNING_MINUTE;
    return this.advanceTicks(minutes * this.config.ticksPerGameMinute);
  }

  calculateOfflineProgress(now = Date.now()): OfflineProgress {
    const elapsed = Math.max(0, now - this.state.lastRealTimestamp);
    // 休止中の庭は時間が動かない。復帰させたときに一気に進まないよう、経過を数えない。
    if (this.state.paused) {
      return { elapsedRealMilliseconds: elapsed, appliedRealMilliseconds: 0, elapsedGameTicks: 0, capped: false, elapsedDays: 0, simulatedDays: 0 };
    }
    const maximum = this.config.maximumOfflineHours * 3600000;
    const applied = Math.min(elapsed, maximum);
    const gameMinutes = (applied / 60000) * (MINUTES_PER_DAY / this.config.realMinutesPerGameDay);
    let ticks = Math.floor(gameMinutes * this.config.ticksPerGameMinute);
    let elapsedDays = Math.floor((this.state.minuteOfDay + Math.floor(ticks / this.config.ticksPerGameMinute)) / MINUTES_PER_DAY);

    // カレンダーの進みにも上限を設ける。実時間だけを 72 時間で切っていたため、
    // 1 日空けただけで 480 日（13 年目）進むなど、年数だけが異常に伸びていた。
    const calendarCap = Math.max(0, Math.floor(this.config.maximumOfflineCalendarDays));
    const calendarCapped = elapsedDays > calendarCap;
    if (calendarCapped) {
      // ちょうど上限日数ぶんに切り詰める。時刻（minuteOfDay）は変えずに日付だけ進める。
      elapsedDays = calendarCap;
      ticks = calendarCap * MINUTES_PER_DAY * this.config.ticksPerGameMinute;
    }
    return {
      elapsedRealMilliseconds: elapsed,
      appliedRealMilliseconds: applied,
      elapsedGameTicks: ticks,
      capped: elapsed > maximum || calendarCapped,
      elapsedDays,
      simulatedDays: Math.min(elapsedDays, Math.max(0, Math.floor(this.config.maximumOfflineDays))),
    };
  }

  applyOfflineProgress(now = Date.now()): { progress: OfflineProgress; events: TimeEvent[] } {
    const base = this.calculateOfflineProgress(now);
    if (this.state.paused) {
      // 休止中でも基準時刻だけは進めておく。次に再開したとき差分がゼロから始まる。
      this.state.lastRealTimestamp = now;
      return { progress: base, events: [] };
    }
    const replay = this.advanceBulk(base.elapsedGameTicks, this.config.maximumOfflineDays);
    this.state.lastRealTimestamp = now;
    return { progress: { ...base, elapsedDays: replay.elapsedDays, simulatedDays: replay.simulatedDays }, events: replay.events };
  }

  /**
   * オフライン経過をまとめて適用する。
   *
   * advanceTicks の 1 分刻みループは通常プレイの微小な delta 向けで、
   * 復帰時の数十万〜数百万 tick をそのまま流すとメインスレッドが固まり、
   * 庭にも数百日ぶんの減衰が一度に入ってしまう。
   * ここではカレンダー（日・季節・年）は算術で正しく進めたうえで、
   * 実際に走らせる日次処理を maximumOfflineDays 日ぶんに制限する。
   */
  private advanceBulk(ticks: number, maximumDayEvents: number): { events: TimeEvent[]; elapsedDays: number; simulatedDays: number } {
    const events: TimeEvent[] = [];
    const whole = Math.floor(ticks);
    if (!Number.isFinite(whole) || whole <= 0) return { events, elapsedDays: 0, simulatedDays: 0 };

    this.state.totalTicks += whole;
    const totalMinutes = this.state.minuteOfDay + Math.floor(whole / this.config.ticksPerGameMinute);
    const days = Math.floor(totalMinutes / MINUTES_PER_DAY);
    this.state.minuteOfDay = totalMinutes % MINUTES_PER_DAY;
    if (days <= 0) return { events, elapsedDays: 0, simulatedDays: 0 };

    const previousSeason = this.state.season;
    const previousYear = this.state.yearNumber;
    const startIndex = Math.max(0, SEASONS.indexOf(previousSeason));
    const seasonDayIndex = this.state.daysInSeason - 1 + days;
    const seasonsAdvanced = Math.floor(seasonDayIndex / this.config.daysPerSeason);
    this.state.day += days;
    this.state.daysInSeason = (seasonDayIndex % this.config.daysPerSeason) + 1;
    this.state.season = SEASONS[(startIndex + seasonsAdvanced) % SEASONS.length] ?? 'spring';
    this.state.yearNumber += Math.floor((startIndex + seasonsAdvanced) / SEASONS.length);

    // 先に季節・年を確定させてから日次処理を回すので、庭は復帰後の季節で評価される。
    if (this.state.yearNumber !== previousYear) this.push(events, { type: 'year', state: this.snapshot });
    if (this.state.season !== previousSeason) this.push(events, { type: 'season', previous: previousSeason, current: this.state.season, state: this.snapshot });

    const simulatedDays = Math.min(days, Math.max(0, Math.floor(maximumDayEvents)));
    for (let index = 0; index < simulatedDays; index += 1) this.push(events, { type: 'day', state: this.snapshot });
    return { events, elapsedDays: days, simulatedDays };
  }

  markSaved(now = Date.now()): void { this.state.lastRealTimestamp = now; }

  private normalizeState(): void {
    this.state.minuteOfDay = Math.max(0, Math.min(MINUTES_PER_DAY - 1, Math.floor(this.state.minuteOfDay)));
    this.state.daysInSeason = Math.max(1, Math.min(this.config.daysPerSeason, Math.floor(this.state.daysInSeason)));
    this.state.day = Math.max(1, Math.floor(this.state.day));
    this.state.yearNumber = Math.max(1, Math.floor(this.state.yearNumber));
    this.state.totalTicks = Math.max(0, Math.floor(this.state.totalTicks));
    if (this.state.timeScale !== 1 && this.state.timeScale !== 2 && this.state.timeScale !== 5) this.state.timeScale = 1;
  }

  private push(events: TimeEvent[], event: TimeEvent): void { events.push(event); for (const listener of this.listeners) listener(event); }
}
